import React, { useEffect, useState } from "react"
import { formatDateDataGrid } from "src/utils/format-date"
import PropTypes from 'prop-types'
import DatalakeService from "src/services/datalake-service"
import dayjs from "dayjs"
import { TIME_FORMAT } from "src/utils/constants"
import ParameterPaginationTable from "src/components/table/parameter-pagination-table"

const toNullableInt = (value) => {
    if (value === null || value === undefined || `${value}`.trim() === "") return null
    const parsed = parseInt(value, 10)
    return Number.isNaN(parsed) ? null : parsed
}

const ParameterDatalakeCETIPOperacoesTable = (props) => {
    const datalakeService = new DatalakeService()
    const [data, setData] = useState([])
    const [pagination, setPagination] = useState({ count: 0, next: null, previous: null })

    useEffect(() => {
        loadData()
    }, [])

    const loadData = () => {
        props.loading(true)
        datalakeService.loadPaginationEndpoint(props.endpoint).then((response) => {
            props.loading(false)

            const { body } = response
            const { results, count, next, previous } = body
            const list = results.map((obj) => ({ ...obj, id: obj.ID }))

            setPagination({ count: count, next: next, previous: previous })
            setData(list)
        }).catch((err) => {
            props.loading(false)
            props.callbackError(err)
        }).finally(() => {
            props.loading(false)
        })
    }

    const createRow = async () => {
        const body = {
            "DataReferencia": null,
            "CETIP": null,
            "CodigoOperacao": null,
            "TipoOperacao": null,
            "CompraVenda": null,
            "TipoCompraVenda": null,
            "TipoAtivo": null,
            "TipoRegime": null,
            "EventosCursadospelaCETIP": null,
            "DataEmissao": null,
            "DataVencimento": null,
            "Quantidade": null,
            "PU": null,
            "ValorFinanceiro": null,
            "TaxaColocacao": null,
            "Sistema": null,
            "ModalidadeLiquidacao": null,
            "Status": null,
            "NumeroOperacao": null,
            "NumeroAssociacao": null,
            "DataLiquidacao": null,
            "DataOrigem": null,
            "InstituicaoConfirmadoraConta": null,
            "InstituicaoConfirmadoraPapel": null,
            "ContraparteNome": null,
            "ContraparteConta": null,
            "DataCompromisso": null,
            "OperacaoOriginalNumero": null,
            "OperacaoOriginalData": null,
            "OperacaoOriginalPU": null,
            "OperacaoOriginalQtd": null,
            "ISPBContraparte": null,
            "OperacaoMensagemNU": null,
            "OperacaoControleNumero": null,
            "Horario": null,
            "ProgramaEmissao": null,
            "OperacaoFuturaNrCtrl": null,
            "Fonte": null
        }

        props.loading(true)
        return datalakeService.createTableEndpoint(props.endpoint, body).then(() => {
            props.loading(false)
            loadData(0, 25)
        }).catch((err) => {
            props.loading(false)
            throw err
        })
    }

    const updateRow = async (newRow) => {
        const { ID, DataReferencia, CETIP, CodigoOperacao, TipoOperacao, CompraVenda, TipoCompraVenda,
            TipoAtivo, TipoRegime, EventosCursadospelaCETIP, DataEmissao, DataVencimento,
            Quantidade, PU, ValorFinanceiro, TaxaColocacao, Sistema, ModalidadeLiquidacao,
            Status, NumeroOperacao, NumeroAssociacao, DataLiquidacao, DataOrigem,
            InstituicaoConfirmadoraConta, InstituicaoConfirmadoraPapel, ContraparteNome,
            ContraparteConta, DataCompromisso, OperacaoOriginalNumero, OperacaoOriginalData,
            OperacaoOriginalPU, OperacaoOriginalQtd, ISPBContraparte, OperacaoMensagemNU,
            OperacaoControleNumero, Horario, ProgramaEmissao, OperacaoFuturaNrCtrl, Fonte
        } = newRow

        const body = {
            "DataReferencia": DataReferencia,
            "CETIP": CETIP,
            "CodigoOperacao": toNullableInt(CodigoOperacao),
            "TipoOperacao": TipoOperacao,
            "CompraVenda": CompraVenda,
            "TipoCompraVenda": TipoCompraVenda,
            "TipoAtivo": TipoAtivo,
            "TipoRegime": TipoRegime,
            "EventosCursadospelaCETIP": EventosCursadospelaCETIP,
            "DataEmissao": DataEmissao,
            "DataVencimento": DataVencimento,
            "Quantidade": Quantidade,
            "PU": PU,
            "ValorFinanceiro": ValorFinanceiro,
            "TaxaColocacao": TaxaColocacao,
            "Sistema": Sistema,
            "ModalidadeLiquidacao": ModalidadeLiquidacao,
            "Status": Status,
            "NumeroOperacao": NumeroOperacao,
            "NumeroAssociacao": NumeroAssociacao,
            "DataLiquidacao": DataLiquidacao,
            "DataOrigem": DataOrigem,
            "InstituicaoConfirmadoraConta": InstituicaoConfirmadoraConta,
            "InstituicaoConfirmadoraPapel": InstituicaoConfirmadoraPapel,
            "ContraparteNome": ContraparteNome,
            "ContraparteConta": ContraparteConta,
            "DataCompromisso": DataCompromisso,
            "OperacaoOriginalNumero": OperacaoOriginalNumero,
            "OperacaoOriginalData": OperacaoOriginalData,
            "OperacaoOriginalPU": OperacaoOriginalPU,
            "OperacaoOriginalQtd": OperacaoOriginalQtd,
            "ISPBContraparte": ISPBContraparte,
            "OperacaoMensagemNU": OperacaoMensagemNU,
            "OperacaoControleNumero": OperacaoControleNumero,
            "Horario": Horario,
            "ProgramaEmissao": ProgramaEmissao,
            "OperacaoFuturaNrCtrl": OperacaoFuturaNrCtrl,
            "Fonte": Fonte
        }

        props.loading(true)
        return datalakeService.updateTableEndpoint(props.endpoint, ID, body).then(() => {
            props.loading(false)
        }).catch((err) => {
            props.loading(false)
            throw err
        })
    }

    const deleteRow = async (row) => {
        props.loading(true)
        const { ID } = row
        return datalakeService.deleteTableEndpoint(props.endpoint, ID).then(() => {
            props.loading(false)
            const listRemoved = data.filter((item) => item.ID !== ID)
            setData(listRemoved)
        }).catch((err) => {
            props.loading(false)
            throw err
        })
    }


    const onFilter = async (filterList, limit, offset) => {
        const query = filterList.map(obj => `${encodeURIComponent(obj.field)}=${encodeURIComponent(obj.value)}`).join('&')

        props.loading(true)
        datalakeService.loadPaginationEndpoint(props.endpoint, query, limit, offset).then((response) => {
            props.loading(false)
            const { body } = response
            const { results, count, next, previous } = body
            const list = results.map((obj) => ({ ...obj, id: obj.ID }))
            setPagination({ count: count, next: next, previous: previous })
            setData(list)
        })
    }

    const changePage = async (endpoint) => {
        props.loading(true)
        datalakeService.paginate(endpoint).then((response) => {
            props.loading(false)
            const { body } = response
            const { results, count, previous, next } = body
            const list = results.map((obj) => ({ ...obj, id: obj.ID }))

            setPagination({ count: count, next: next, previous: previous })
            setData([])
            setData(list)
        })
    }

    const columns = [
        {
            field: 'ID',
            headerName: '#',
            disableColumnMenu: true,
            disableFilter: true,
            flex: true,
            minWidth: 80,
        },
        {
            field: 'DataReferencia',
            headerName: 'DataReferencia',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'date',
        },
        {
            field: 'CETIP',
            headerName: 'CETIP',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'CodigoOperacao',
            headerName: 'CodigoOperacao',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'number',
        },
        {
            field: 'TipoOperacao',
            headerName: 'TipoOperacao',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'CompraVenda',
            headerName: 'CompraVenda',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'TipoCompraVenda',
            headerName: 'TipoCompraVenda',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'TipoAtivo',
            headerName: 'TipoAtivo',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'TipoRegime',
            headerName: 'TipoRegime',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'EventosCursadospelaCETIP',
            headerName: 'EventosCursadospelaCETIP',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'DataEmissao',
            headerName: 'DataEmissao',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'date',
        },
        {
            field: 'DataVencimento',
            headerName: 'DataVencimento',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'date',
        },
        {
            field: 'Quantidade',
            headerName: 'Quantidade',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'number',
        },
        {
            field: 'PU',
            headerName: 'PU',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'number',
        },
        {
            field: 'ValorFinanceiro',
            headerName: 'ValorFinanceiro',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'number',
        },
        {
            field: 'TaxaColocacao',
            headerName: 'TaxaColocacao',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'number',
        },
        {
            field: 'Sistema',
            headerName: 'Sistema',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'ModalidadeLiquidacao',
            headerName: 'ModalidadeLiquidacao',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'Status',
            headerName: 'Status',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'NumeroOperacao',
            headerName: 'NumeroOperacao',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'number',
        },
        {
            field: 'NumeroAssociacao',
            headerName: 'NumeroAssociacao',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'number',
        },
        {
            field: 'DataLiquidacao',
            headerName: 'DataLiquidacao',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'date',
        },
        {
            field: 'DataOrigem',
            headerName: 'DataOrigem',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'date',
        },
        {
            field: 'InstituicaoConfirmadoraConta',
            headerName: 'InstituicaoConfirmadoraConta',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'InstituicaoConfirmadoraPapel',
            headerName: 'InstituicaoConfirmadoraPapel',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'ContraparteNome',
            headerName: 'ContraparteNome',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'ContraparteConta',
            headerName: 'ContraparteConta',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'DataCompromisso',
            headerName: 'DataCompromisso',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'date',
        },
        {
            field: 'OperacaoOriginalNumero',
            headerName: 'OperacaoOriginalNumero',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'OperacaoOriginalData',
            headerName: 'OperacaoOriginalData',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'date',
        },
        {
            field: 'OperacaoOriginalPU',
            headerName: 'OperacaoOriginalPU',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'number',
        },
        {
            field: 'OperacaoOriginalQtd',
            headerName: 'OperacaoOriginalQtd',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'number',
        },
        {
            field: 'ISPBContraparte',
            headerName: 'ISPBContraparte',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'OperacaoMensagemNU',
            headerName: 'OperacaoMensagemNU',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'OperacaoControleNumero',
            headerName: 'OperacaoControleNumero',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'Horario',
            headerName: 'Horario',
            flex: 1,
            minWidth: 150,
            disableFilter: true,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'time',
            renderCell: (params) => {
                if (!params.value) {
                    return ''
                }
                return dayjs(params.value).format(TIME_FORMAT)
            },
        },
        {
            field: 'ProgramaEmissao',
            headerName: 'ProgramaEmissao',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'OperacaoFuturaNrCtrl',
            headerName: 'OperacaoFuturaNrCtrl',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'Fonte',
            headerName: 'Fonte',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'CriadoEm',
            headerName: 'Data de Criação',
            flex: true,
            minWidth: 150,
            disableColumnMenu: true,
            disableFilter: true,
            renderCell: (params) => formatDateDataGrid(params)
        },
        {
            field: 'AtualizadoEm',
            headerName: 'Data de Atualização',
            flex: true,
            minWidth: 150,
            disableColumnMenu: true,
            disableFilter: true,
            renderCell: (params) => formatDateDataGrid(params)
        }
    ]

    return (
        <div>
            <ParameterPaginationTable
                columns={columns}
                data={data}
                setData={setData}
                createRow={createRow}
                deleteRow={deleteRow}
                updateRow={updateRow}
                pagination={pagination}
                onChangePage={changePage}
                filterFunction={onFilter}
                customFilter={true} />
        </div>
    )
}

ParameterDatalakeCETIPOperacoesTable.propTypes = {
    loading: PropTypes.func,
    endpoint: PropTypes.string,
    callbackError: PropTypes.func
}

export default ParameterDatalakeCETIPOperacoesTable